import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { uploadSavegame, confirmUpload, type UploadInspection } from '../../api/games';

interface Props {
  gameId: string | null;
  gameName: string;
  onClose: () => void;
}

const DRAWER_WIDTH = 480;

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

// ── Preview confirm dialog ─────────────────────────────────────────────────

function PreviewDialog({
  inspection,
  onClose,
  onConfirm,
}: {
  inspection: UploadInspection;
  onClose: () => void;
  onConfirm: (restartAfter: boolean) => void;
}) {
  const [restartAfter, setRestartAfter] = useState(false);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm savegame restore</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will stop the server and overwrite current save data.
        </Alert>
        {inspection.warnings.map((w, i) => (
          <Alert key={i} severity="info" sx={{ mb: 1 }}>{w}</Alert>
        ))}
        <Typography variant="body2" gutterBottom>
          <strong>{inspection.entry_count}</strong> entries,{' '}
          <strong>{fmtBytes(inspection.total_uncompressed_bytes)}</strong> uncompressed
        </Typography>
        <Box sx={{ maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: '0.73rem', fontFamily: 'monospace' }}>
          {inspection.entries.slice(0, 100).map((e) => (
            <div key={e.path}>{e.path}</div>
          ))}
          {inspection.entries.length > 100 && (
            <div>… and {inspection.entries.length - 100} more entries</div>
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={restartAfter}
              onChange={(e) => setRestartAfter(e.target.checked)}
              size="small"
            />
          }
          label="Restart server after restore"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={() => onConfirm(restartAfter)}>
          Extract &amp; Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main upload drawer ─────────────────────────────────────────────────────

export function UploadDrawer({ gameId, gameName, onClose }: Props) {
  const open = gameId !== null;

  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [inspection, setInspection] = useState<UploadInspection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setUploadProgress(null);
    setInspection(null);
    setError(null);
    setConfirming(false);
    setDone(false);
  };

  const handleFile = useCallback(async (file: File) => {
    if (!gameId) return;
    setError(null);
    setInspection(null);
    setDone(false);
    setUploadProgress(0);

    try {
      const result = await uploadSavegame(gameId, file, setUploadProgress);
      setUploadProgress(100);
      setInspection(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      setUploadProgress(null);
    }
  }, [gameId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }, [handleFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  const handleConfirm = async (restartAfter: boolean) => {
    if (!gameId || !inspection) return;
    setConfirming(true);
    setInspection(null);
    try {
      await confirmUpload(gameId, inspection.upload_token, restartAfter);
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: DRAWER_WIDTH } } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, minHeight: 56 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
            Upload Savegame — {gameName}
          </Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Divider />

        <Box sx={{ p: 2 }}>
          {done ? (
            <Alert severity="success">Savegame restored successfully.</Alert>
          ) : confirming ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2">Extracting savegame…</Typography>
            </Stack>
          ) : (
            <>
              {/* Drop zone */}
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                component="label"
                htmlFor="savegame-upload-input"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: dragging ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 4,
                  cursor: 'pointer',
                  bgcolor: dragging ? 'action.selected' : 'transparent',
                  transition: 'all 0.15s',
                  mb: 2,
                }}
              >
                <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Drag &amp; drop a .tar.gz file here, or click to select
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Max {/* uploaded from config */}30 MB
                </Typography>
                <input
                  id="savegame-upload-input"
                  type="file"
                  accept=".tar.gz,.tgz"
                  hidden
                  onChange={handleInputChange}
                />
              </Box>

              {uploadProgress !== null && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Inspecting tarball…'}
                  </Typography>
                </Box>
              )}

              {error && <Alert severity="error">{error}</Alert>}
            </>
          )}
        </Box>
      </Drawer>

      {inspection && gameId && (
        <PreviewDialog
          inspection={inspection}
          onClose={() => setInspection(null)}
          onConfirm={(restartAfter) => void handleConfirm(restartAfter)}
        />
      )}
    </>
  );
}
